import React, { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { api, uploadQrMenuImage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Building2,
  ChevronRight,
  KeyRound,
  MapPin,
  Upload,
  LogOut,
  Trash2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const getStoredUser = () => {
  try {
    const rawUser = localStorage.getItem("user");
    return rawUser ? JSON.parse(rawUser) : null;
  } catch {
    return null;
  }
};

const getBusinessId = (user) =>
  user?.businessId ?? user?.business_id ?? user?.BusinessId ?? "";

const getBusinessSubDomain = (business, user) =>
  business?.subDomain ||
  business?.SubDomain ||
  user?.businessSubDomain ||
  user?.BusinessSubDomain ||
  user?.subDomain ||
  user?.SubDomain ||
  "";

const getTypeConstantValue = (business, user) =>
  business?.typeConstantValue ||
  business?.TypeConstantValue ||
  business?.businessTypeConstantValue ||
  business?.BusinessTypeConstantValue ||
  business?.businessSector ||
  business?.BusinessSector ||
  business?.businessType ||
  business?.BusinessType ||
  business?.typeConstant?.value ||
  business?.TypeConstant?.Value ||
  user?.typeConstantValue ||
  user?.TypeConstantValue ||
  user?.businessTypeConstantValue ||
  user?.BusinessTypeConstantValue ||
  user?.businessSector ||
  user?.BusinessSector ||
  user?.businessType ||
  user?.BusinessType ||
  user?.business?.typeConstantValue ||
  user?.business?.TypeConstantValue ||
  user?.business?.typeConstant?.value ||
  user?.business?.TypeConstant?.Value ||
  "";

const getUserEmail = (user, fallbackEmail = "") =>
  user?.email ||
  user?.Email ||
  user?.username ||
  user?.Username ||
  fallbackEmail ||
  "";

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.detail ||
  error?.response?.data?.message ||
  error?.response?.data?.title ||
  error?.message ||
  fallback;

export default function ProfileSettings() {
  const { logout, deleteAccount } = useAuth();

  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordCodeLoading, setPasswordCodeLoading] = useState(false);
  const [constantsLoading, setConstantsLoading] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [currentUser, setCurrentUser] = useState(null);
  const [currentBusiness, setCurrentBusiness] = useState(null);
  const [businessSectors, setBusinessSectors] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [mobilePanel, setMobilePanel] = useState(null);
  const [passwordStep, setPasswordStep] = useState(1);

  const [formData, setFormData] = useState({
    name: "",
    logo_url: "",
    contact_phone: "",
    contact_address: "",
    email: "",
    type_constant_value: "",
    popup_title: "",
    popup_description: "",
    instagram_url: "",
    review_url: "",
    maps_url: "",
  });

  const [passwordCode, setPasswordCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordAgain, setNewPasswordAgain] = useState("");

  const passwordEmail = useMemo(
    () => getUserEmail(currentUser, formData.email).trim().toLowerCase(),
    [currentUser, formData.email]
  );

  const selectedSectorName = useMemo(
    () =>
      businessSectors.find(
        (item) => item.value === formData.type_constant_value
      )?.name ||
      formData.type_constant_value ||
      "İşletme türünü seçin",
    [businessSectors, formData.type_constant_value]
  );

  const popupConfigured = Boolean(
    formData.popup_title.trim() ||
      formData.instagram_url.trim() ||
      formData.review_url.trim() ||
      formData.maps_url.trim()
  );

  const rawBusinessLogo =
    currentBusiness?.logoUrl ||
    currentBusiness?.logo_url ||
    currentBusiness?.imageUrl ||
    currentBusiness?.ImageUrl ||
    "";

  const businessLogo =
    typeof rawBusinessLogo === "string" &&
    /^https?:\/\//i.test(rawBusinessLogo)
      ? rawBusinessLogo
      : "";

  useEffect(() => {
    fetchProfile();
    loadBusinessSectors();
  }, []);

  const updateFormField = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const loadBusinessSectors = async () => {
    try {
      setConstantsLoading(true);

      const res = await api.getPublicConstants({
        type: "BUSINESS_SECTOR",
        pageSize: 1000,
      });

      setBusinessSectors(res?.items || []);
    } catch (error) {
      console.error("BUSINESS SECTORS LOAD ERROR:", error);
      toast.error("İşletme sektörleri yüklenemedi.");
    } finally {
      setConstantsLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const user = getStoredUser();

      if (!user) {
        toast.error("Kullanıcı bilgisi bulunamadı.");
        return;
      }

      setCurrentUser(user);

      const businessId = getBusinessId(user);

      if (!businessId) {
        toast.error("İşletme ID bulunamadı.");
        return;
      }

      const response = await api.getBusinessById(businessId);
      const data = response?.data || {};

      setCurrentBusiness(data);

      let experienceData = null;

      try {
        const popupResponse = await api.getPopupList({
          "PageRequest.Page": 0,
          "PageRequest.PageSize": 1,
        });

        const popupItems = Array.isArray(popupResponse?.data?.items)
          ? popupResponse.data.items
          : Array.isArray(popupResponse?.data)
          ? popupResponse.data
          : [];

        const popup = popupItems[0] || null;

        if (popup) {
          experienceData = {
            title: popup.title || "",
            description: popup.description || "",
            instagramUrl: popup.instagramUrl || "",
            reviewUrl: popup.reviewUrl || "",
            mapsUrl: popup.locationUrl || "",
          };
        }
      } catch (popupError) {
        console.error("POPUP FETCH ERROR:", popupError);
      }

      setFormData({
        name: data.name || data.Name || "",
        logo_url:
          data.imageId || data.ImageId ? String(data.imageId || data.ImageId) : "",
        contact_phone: data.phone || data.Phone || "",
        contact_address: data.address || data.Address || "",
        email: getUserEmail(user, data.email || data.Email),
        type_constant_value: getTypeConstantValue(data, user),
        popup_title: experienceData?.title || "",
        popup_description: experienceData?.description || "",
        instagram_url: experienceData?.instagramUrl || "",
        review_url: experienceData?.reviewUrl || "",
        maps_url: experienceData?.mapsUrl || "",
      });
    } catch (error) {
      console.error("PROFILE FETCH ERROR:", error);
      toast.error("İşletme bilgileri alınırken bir hata oluştu.");
    }
  };

  const savePopupExperience = async () => {
    const popupResponse = await api.getPopupList({
      "PageRequest.Page": 0,
      "PageRequest.PageSize": 1,
    });

    const popupItems = Array.isArray(popupResponse?.data?.items)
      ? popupResponse.data.items
      : Array.isArray(popupResponse?.data)
      ? popupResponse.data
      : [];

    const existingPopup = popupItems[0] || null;

    const payload = {
      title: formData.popup_title.trim(),
      description: formData.popup_description.trim() || null,
      instagramUrl: formData.instagram_url.trim() || null,
      reviewUrl: formData.review_url.trim() || null,
      locationUrl: formData.maps_url.trim() || null,
    };

    if (existingPopup?.id != null) {
      await api.updatePopup({
        id: existingPopup.id,
        ...payload,
      });

      return;
    }

    await api.addPopup(payload);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = currentUser || getStoredUser();

      if (!user) {
        toast.error("Kullanıcı bilgisi bulunamadı.");
        return;
      }

      const businessId = getBusinessId(user);

      if (!businessId) {
        toast.error("İşletme ID bulunamadı.");
        return;
      }

      if (!formData.name.trim()) {
        toast.error("İşletme adı zorunludur.");
        return;
      }

      const email = formData.email || getUserEmail(user);

      if (!email) {
        toast.error("Email bilgisi bulunamadı.");
        return;
      }

      const selectedTypeConstantValue =
        formData.type_constant_value || getTypeConstantValue(currentBusiness, user);

      if (!selectedTypeConstantValue) {
        toast.error("Lütfen işletme türünü seçin.");
        return;
      }

      let logoId =
        currentBusiness?.imageId ||
        currentBusiness?.ImageId ||
        (formData.logo_url ? Number(formData.logo_url) : undefined);

      if (logoFile) {
        const uploaded = await uploadQrMenuImage(logoFile);

        logoId =
          uploaded?.id ||
          uploaded?.Id ||
          uploaded?.data?.id ||
          uploaded?.data?.Id ||
          uploaded?.data?.data?.id ||
          uploaded?.data?.data?.Id;

        if (!logoId) {
          console.warn("LOGO UPLOAD RESPONSE:", uploaded);
          toast.error("Logo yüklendi ancak görsel ID alınamadı.");
          return;
        }
      }

      const updateRes = await api.updateBusiness({
        id: businessId,
        name: formData.name,
        address: formData.contact_address,
        phone: formData.contact_phone,
        email,
        imageId: logoId,
        TypeConstantValue: selectedTypeConstantValue,
      });

      await savePopupExperience();

      toast.success("Profil başarıyla güncellendi.");
      setLogoFile(null);
      await fetchProfile();
      setMobilePanel(null);
    } catch (error) {
      console.error("PROFILE UPDATE ERROR:", error);
      toast.error(getErrorMessage(error, "Profil güncellenirken bir hata oluştu."));
    } finally {
      setLoading(false);
    }
  };

  const handlePopupSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await savePopupExperience();

      toast.success("Müşteri popup başarıyla güncellendi.");
      setMobilePanel(null);
    } catch (error) {
      console.error("POPUP UPDATE ERROR:", error);

      const message =
        error?.response?.data?.Detail ||
        error?.response?.data?.detail ||
        error?.response?.data?.Message ||
        error?.response?.data?.message ||
        error?.response?.data?.Title ||
        error?.response?.data?.title ||
        error?.message ||
        "Müşteri popup güncellenirken bir hata oluştu.";

      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendPasswordCode = async () => {
    if (!passwordEmail) {
      toast.error("E-posta bilgisi bulunamadı.");
      return;
    }

    try {
      setPasswordCodeLoading(true);

      await api.forgotPassword({
        email: passwordEmail,
      });

      toast.success("Doğrulama kodu e-posta adresinize gönderildi.");
      setPasswordStep(2);
    } catch (error) {
      console.error("PASSWORD CODE SEND ERROR:", error);
      toast.error(getErrorMessage(error, "Kod gönderilirken bir hata oluştu."));
    } finally {
      setPasswordCodeLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    const cleanOtp = passwordCode.trim();

    if (!passwordEmail) {
      toast.error("E-posta bilgisi bulunamadı.");
      return;
    }

    if (!cleanOtp) {
      toast.error("Lütfen doğrulama kodunu girin.");
      return;
    }

    if (!newPassword || newPassword.length < 6) {
      toast.error("Yeni şifre en az 6 karakter olmalıdır.");
      return;
    }

    if (newPassword !== newPasswordAgain) {
      toast.error("Yeni şifre ve tekrar aynı olmalıdır.");
      return;
    }

    try {
      setPasswordLoading(true);

      const verifyRes = await api.validateOtp({
        otp: cleanOtp,
      });

      const validateToken = verifyRes?.data?.validateToken;

      if (!validateToken) {
        toast.error("Doğrulama tokenı alınamadı.");
        return;
      }

      await api.changeForgotPassword({
        newPassword,
        validateToken,
      });

      toast.success("Şifreniz başarıyla güncellendi.");

      setPasswordCode("");
      setNewPassword("");
      setNewPasswordAgain("");
      setPasswordStep(1);
      setMobilePanel(null);
    } catch (error) {
      console.error("PASSWORD CHANGE ERROR:", error);
      toast.error(getErrorMessage(error, "Şifre değiştirilirken bir hata oluştu."));
    } finally {
      setPasswordLoading(false);
    }
  };

  const closeMobilePanel = () => {
    const closingPanel = mobilePanel;

    setMobilePanel(null);
    setLogoFile(null);

    if (closingPanel === "profile" || closingPanel === "popup") {
      fetchProfile();
    }

    if (closingPanel === "password") {
      setPasswordStep(1);
      setPasswordCode("");
      setNewPassword("");
      setNewPasswordAgain("");
    }
  };

  const handleMobilePanelOpenChange = (open) => {
    if (!open) {
      closeMobilePanel();
    }
  };

  const openPasswordPanel = () => {
    setPasswordStep(1);
    setPasswordCode("");
    setNewPassword("");
    setNewPasswordAgain("");
    setMobilePanel("password");
  };

  const handleMobileLogout = async () => {
    if (logoutLoading) return;

    const confirmed = window.confirm(
      "Hesabınızdan çıkış yapmak istiyor musunuz?"
    );

    if (!confirmed) return;

    try {
      setLogoutLoading(true);
      await logout();
    } catch (error) {
      console.error("LOGOUT ERROR:", error);
      toast.error("Çıkış işlemi tamamlanamadı.");
      setLogoutLoading(false);
    }
  };

  const handleMobileDeleteAccount = async () => {
    if (deleteLoading) return;

    const firstConfirm = window.confirm(
      "Hesabınızı silmek üzeresiniz. Bu işlem geri alınamaz. Devam etmek istiyor musunuz?"
    );

    if (!firstConfirm) return;

    const finalConfirm = window.confirm(
      "Son onay: Hesabınız kalıcı olarak silinecek. Hesabı kalıcı olarak silmek istiyor musunuz?"
    );

    if (!finalConfirm) return;

    try {
      setDeleteLoading(true);
      await deleteAccount();
    } catch (error) {
      console.error("ACCOUNT DELETE ERROR:", error);
      toast.error(
        getErrorMessage(error, "Hesap silme işlemi tamamlanamadı.")
      );
      setDeleteLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div
        className="min-h-full bg-[#F8F8F8] px-5 pb-10 pt-4 md:hidden"
        data-testid="profile-settings-mobile"
      >
        <div className="mb-6 flex min-h-[96px] items-center gap-3 rounded-[20px] border border-[#E4E4E7] bg-white p-4">
          {businessLogo ? (
            <img
              src={businessLogo}
              alt={formData.name || "İşletme logosu"}
              className="h-[58px] w-[58px] shrink-0 rounded-[17px] border border-[#E4E4E7] bg-[#F4F4F5] object-cover"
            />
          ) : (
            <div className="flex h-[58px] w-[58px] shrink-0 items-center justify-center rounded-[17px] bg-[#FFF7ED] text-2xl font-extrabold text-[#EA580C]">
              {formData.name.trim().charAt(0).toLocaleUpperCase("tr-TR") || "İ"}
            </div>
          )}

          <div className="min-w-0 flex-1">
            <h1 className="truncate text-[19px] font-extrabold leading-6 text-[#18181B]">
              {formData.name || "İşletme"}
            </h1>
            <p className="mt-0.5 truncate text-xs font-semibold text-[#52525B]">
              {selectedSectorName}
            </p>
            {formData.contact_address ? (
              <p className="mt-0.5 truncate text-[11px] text-[#A1A1AA]">
                {formData.contact_address}
              </p>
            ) : null}
          </div>
        </div>

        <p className="mb-2 ml-1 text-[10px] font-extrabold tracking-[0.07em] text-[#A1A1AA]">
          İŞLETME
        </p>

        <div className="mb-6 overflow-hidden rounded-[18px] border border-[#E4E4E7] bg-white">
          <button
            type="button"
            onClick={() => setMobilePanel("profile")}
            className="flex min-h-[72px] w-full items-center gap-3 px-4 py-3 text-left active:bg-[#FAFAFA]"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#FFF7ED] text-[#EA580C]">
              <Building2 className="h-[18px] w-[18px]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-bold leading-5 text-[#18181B]">
                İşletme Profili
              </p>
              <p className="mt-0.5 truncate text-[11px] leading-4 text-[#71717A]">
                İsim, logo, işletme türü ve iletişim
              </p>
            </div>
            <ChevronRight className="h-5 w-5 shrink-0 text-[#C4C4C8]" />
          </button>

          <div className="ml-16 h-px bg-[#E4E4E7]" />

          <button
            type="button"
            onClick={() => setMobilePanel("popup")}
            className="flex min-h-[72px] w-full items-center gap-3 px-4 py-3 text-left active:bg-[#FAFAFA]"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#EFF6FF] text-[#2563EB]">
              <MapPin className="h-[18px] w-[18px]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-bold leading-5 text-[#18181B]">
                QR Müşteri Popup
              </p>
              <p className="mt-0.5 truncate text-[11px] leading-4 text-[#71717A]">
                Değerlendirme, sosyal medya ve konum
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              {popupConfigured ? (
                <span className="rounded-full bg-[#FFF7ED] px-2 py-1 text-[9px] font-bold text-[#EA580C]">
                  Hazır
                </span>
              ) : null}
              <ChevronRight className="h-5 w-5 text-[#C4C4C8]" />
            </div>
          </button>
        </div>

        <p className="mb-2 ml-1 text-[10px] font-extrabold tracking-[0.07em] text-[#A1A1AA]">
          HESAP VE GÜVENLİK
        </p>

        <div className="overflow-hidden rounded-[18px] border border-[#E4E4E7] bg-white">
          <button
            type="button"
            onClick={openPasswordPanel}
            className="flex min-h-[72px] w-full items-center gap-3 px-4 py-3 text-left active:bg-[#FAFAFA]"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#F4F4F5] text-[#52525B]">
              <KeyRound className="h-[18px] w-[18px]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-bold leading-5 text-[#18181B]">
                Şifre Değiştir
              </p>
              <p className="mt-0.5 truncate text-[11px] leading-4 text-[#71717A]">
                {passwordEmail || "Hesap güvenliğinizi yönetin"}
              </p>
            </div>
            <ChevronRight className="h-5 w-5 shrink-0 text-[#C4C4C8]" />
          </button>

          <div className="ml-16 h-px bg-[#E4E4E7]" />

          <button
            type="button"
            disabled={deleteLoading}
            onClick={handleMobileDeleteAccount}
            className="flex min-h-[72px] w-full items-center gap-3 px-4 py-3 text-left active:bg-[#FEF2F2] disabled:opacity-50"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#FEF2F2] text-[#DC2626]">
              <Trash2 className="h-[18px] w-[18px]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-bold leading-5 text-[#DC2626]">
                {deleteLoading ? "Hesap Siliniyor..." : "Hesabımı Sil"}
              </p>
              <p className="mt-0.5 truncate text-[11px] leading-4 text-[#71717A]">
                Hesabınızı kalıcı olarak kapatın
              </p>
            </div>
            <ChevronRight className="h-5 w-5 shrink-0 text-[#DC2626]" />
          </button>
        </div>

        <p className="mb-2 ml-1 mt-6 text-[10px] font-extrabold tracking-[0.07em] text-[#A1A1AA]">
          OTURUM
        </p>

        <div className="overflow-hidden rounded-[18px] border border-[#E4E4E7] bg-white">
          <button
            type="button"
            disabled={logoutLoading}
            onClick={handleMobileLogout}
            className="flex min-h-[72px] w-full items-center gap-3 px-4 py-3 text-left active:bg-[#FAFAFA] disabled:opacity-50"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#F4F4F5] text-[#52525B]">
              <LogOut className="h-[18px] w-[18px]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-bold leading-5 text-[#18181B]">
                {logoutLoading ? "Çıkış yapılıyor..." : "Çıkış Yap"}
              </p>
              <p className="mt-0.5 truncate text-[11px] leading-4 text-[#71717A]">
                Bu tarayıcıdaki oturumu sonlandır
              </p>
            </div>
            <ChevronRight className="h-5 w-5 shrink-0 text-[#C4C4C8]" />
          </button>
        </div>
      </div>

      <Dialog
        open={mobilePanel === "profile"}
        onOpenChange={handleMobilePanelOpenChange}
      >
        <DialogContent
          mobileSheet
          className="rounded-t-[24px] bg-white md:hidden"
          style={{
            height: "min(92dvh, 860px)",
            maxHeight: "calc(100dvh - 8px)",
          }}
        >
          <form onSubmit={handleSubmit} className="flex h-full min-h-0 flex-col">
            <DialogHeader className="shrink-0 border-b border-[#E4E4E7] px-5 pb-4 pt-5 text-left">
              <p className="text-[10px] font-extrabold tracking-[0.07em] text-[#EA580C]">
                İŞLETME
              </p>
              <DialogTitle className="text-[23px] font-extrabold leading-7 text-[#18181B]">
                İşletme Profili
              </DialogTitle>
              <DialogDescription className="max-w-[330px] text-[13px] leading-[19px] text-[#71717A]">
                Müşterilerinizin göreceği temel işletme bilgilerini düzenleyin.
              </DialogDescription>
            </DialogHeader>

            <div className="min-h-0 flex-1 touch-pan-y space-y-4 overflow-y-auto overscroll-contain px-5 py-5 [-webkit-overflow-scrolling:touch]">
              <div className="space-y-1.5">
                <Label htmlFor="mobile-name">İşletme adı</Label>
                <Input
                  id="mobile-name"
                  value={formData.name}
                  onChange={(e) => updateFormField("name", e.target.value)}
                  autoComplete="organization"
                  className="h-[52px] rounded-[15px] bg-[#FAFAFA] text-base"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="mobile-email">E-posta</Label>
                <Input
                  id="mobile-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateFormField("email", e.target.value)}
                  autoComplete="email"
                  inputMode="email"
                  className="h-[52px] rounded-[15px] bg-[#FAFAFA] text-base"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="mobile-type">İşletme türü</Label>
                <Select
                  value={formData.type_constant_value}
                  onValueChange={(value) =>
                    updateFormField("type_constant_value", value)
                  }
                  disabled={constantsLoading}
                >
                  <SelectTrigger
                    id="mobile-type"
                    className="h-[52px] rounded-[15px] bg-[#FAFAFA] text-base"
                  >
                    <SelectValue
                      placeholder={
                        constantsLoading
                          ? "İşletme türleri yükleniyor..."
                          : "İşletme türünü seçin"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent className="z-[70] max-h-[280px]">
                    {businessSectors.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="mobile-logo">Logo</Label>
                <input
                  id="mobile-logo"
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                />
                <label
                  htmlFor="mobile-logo"
                  className="flex min-h-[82px] cursor-pointer items-center gap-3 rounded-[16px] border border-[#E4E4E7] bg-[#FAFAFA] p-3 active:bg-[#F4F4F5]"
                >
                  <div className="flex h-[58px] w-[58px] shrink-0 items-center justify-center rounded-[15px] bg-[#FFF7ED] text-[22px] font-extrabold text-[#EA580C]">
                    {formData.name.trim().charAt(0).toLocaleUpperCase("tr-TR") || "İ"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-bold text-[#18181B]">
                      {logoFile ? logoFile.name : "İşletme logosu"}
                    </p>
                    <p className="mt-0.5 text-[10px] leading-[15px] text-[#71717A]">
                      {logoFile
                        ? "Başka bir görsel seçmek için dokunun"
                        : "Galeriden veya dosyalardan görsel seçin"}
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-[#EA580C]">
                    <Upload className="h-4 w-4" />
                    {logoFile ? "Değiştir" : "Seç"}
                  </span>
                </label>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="mobile-phone">İletişim telefonu</Label>
                <Input
                  id="mobile-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={formData.contact_phone}
                  onChange={(e) =>
                    updateFormField("contact_phone", e.target.value)
                  }
                  className="h-[52px] rounded-[15px] bg-[#FAFAFA] text-base"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="mobile-address">Adres</Label>
                <Textarea
                  id="mobile-address"
                  value={formData.contact_address}
                  onChange={(e) =>
                    updateFormField("contact_address", e.target.value)
                  }
                  autoComplete="street-address"
                  className="min-h-[92px] resize-none rounded-[15px] bg-[#FAFAFA] text-base"
                />
              </div>
            </div>

            <div className="relative z-10 grid shrink-0 grid-cols-2 gap-3 border-t border-[#E4E4E7] bg-white px-5 pt-3 pb-[calc(12px+env(safe-area-inset-bottom))]">
              <Button
                type="button"
                variant="outline"
                onClick={closeMobilePanel}
                disabled={loading}
                className="h-[52px] rounded-[15px]"
              >
                Vazgeç
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="h-[52px] rounded-[15px] bg-[#EA580C] hover:bg-[#EA580C]"
              >
                {loading ? "Kaydediliyor..." : "Kaydet"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={mobilePanel === "popup"}
        onOpenChange={handleMobilePanelOpenChange}
      >
        <DialogContent
          mobileSheet
          className="rounded-t-[24px] bg-white md:hidden"
          style={{
            height: "min(92dvh, 860px)",
            maxHeight: "calc(100dvh - 8px)",
          }}
        >
          <form
            onSubmit={handlePopupSubmit}
            className="flex h-full min-h-0 flex-col"
          >
            <DialogHeader className="shrink-0 border-b border-[#E4E4E7] px-5 pb-4 pt-5 text-left">
              <p className="text-[10px] font-extrabold tracking-[0.07em] text-[#EA580C]">
                MÜŞTERİ DENEYİMİ
              </p>
              <DialogTitle className="text-[23px] font-extrabold leading-7 text-[#18181B]">
                Müşteri Popup
              </DialogTitle>
              <DialogDescription className="max-w-[330px] text-[13px] leading-[19px] text-[#71717A]">
                Web Menü’den gelen müşterileri değerlendirme, sosyal medya ve
                konum bağlantılarına yönlendirin.
              </DialogDescription>
            </DialogHeader>

            <div className="min-h-0 flex-1 touch-pan-y space-y-4 overflow-y-auto overscroll-contain px-5 py-5 [-webkit-overflow-scrolling:touch]">
              <div className="space-y-1.5">
                <Label htmlFor="mobile-popup-title">Popup başlığı</Label>
                <Input
                  id="mobile-popup-title"
                  value={formData.popup_title}
                  onChange={(e) =>
                    updateFormField("popup_title", e.target.value)
                  }
                  placeholder="Bizi değerlendirin ve takip edin"
                  className="h-[52px] rounded-[15px] bg-[#FAFAFA] text-base"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="mobile-popup-description">
                  Popup açıklaması
                </Label>
                <Textarea
                  id="mobile-popup-description"
                  value={formData.popup_description}
                  onChange={(e) =>
                    updateFormField("popup_description", e.target.value)
                  }
                  placeholder="Müşterilerinize kısa bir mesaj yazın"
                  className="min-h-[110px] resize-none rounded-[15px] bg-[#FAFAFA] text-base"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="mobile-instagram">Instagram linki</Label>
                <Input
                  id="mobile-instagram"
                  inputMode="url"
                  autoCapitalize="none"
                  autoComplete="url"
                  value={formData.instagram_url}
                  onChange={(e) =>
                    updateFormField("instagram_url", e.target.value)
                  }
                  placeholder="https://instagram.com/isletmehesabi"
                  className="h-[52px] rounded-[15px] bg-[#FAFAFA] text-base"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="mobile-review">Değerlendirme linki</Label>
                <Input
                  id="mobile-review"
                  inputMode="url"
                  autoCapitalize="none"
                  autoComplete="url"
                  value={formData.review_url}
                  onChange={(e) =>
                    updateFormField("review_url", e.target.value)
                  }
                  placeholder="Google yorum / Tripadvisor linki"
                  className="h-[52px] rounded-[15px] bg-[#FAFAFA] text-base"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="mobile-maps">Konum linki</Label>
                <Input
                  id="mobile-maps"
                  inputMode="url"
                  autoCapitalize="none"
                  autoComplete="url"
                  value={formData.maps_url}
                  onChange={(e) =>
                    updateFormField("maps_url", e.target.value)
                  }
                  placeholder="https://maps.google.com/..."
                  className="h-[52px] rounded-[15px] bg-[#FAFAFA] text-base"
                />
              </div>

              <div className="rounded-[15px] bg-[#FFF7ED] p-3.5">
                <p className="text-xs font-bold text-[#EA580C]">
                  Müşteri deneyimi
                </p>
                <p className="mt-1 text-[11px] leading-4 text-[#9A3412]">
                  Boş bıraktığınız bağlantılar müşteriye gösterilmez.
                </p>
              </div>
            </div>

            <div className="relative z-10 grid shrink-0 grid-cols-2 gap-3 border-t border-[#E4E4E7] bg-white px-5 pt-3 pb-[calc(12px+env(safe-area-inset-bottom))]">
              <Button
                type="button"
                variant="outline"
                onClick={closeMobilePanel}
                disabled={loading}
                className="h-[52px] rounded-[15px]"
              >
                Vazgeç
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="h-[52px] rounded-[15px] bg-[#EA580C] hover:bg-[#EA580C]"
              >
                {loading ? "Kaydediliyor..." : "Kaydet"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={mobilePanel === "password"}
        onOpenChange={handleMobilePanelOpenChange}
      >
        <DialogContent
          mobileSheet
          className="rounded-t-[24px] bg-white md:hidden"
          style={{
            height: "min(86dvh, 760px)",
            maxHeight: "calc(100dvh - 8px)",
          }}
        >
          <form onSubmit={handleChangePassword} className="flex h-full min-h-0 flex-col">
            <DialogHeader className="shrink-0 border-b border-[#E4E4E7] px-5 pb-4 pt-5 text-left">
              <p className="text-[10px] font-extrabold tracking-[0.07em] text-[#EA580C]">
                GÜVENLİK
              </p>
              <DialogTitle className="text-[23px] font-extrabold leading-7 text-[#18181B]">
                Şifre Değiştir
              </DialogTitle>
              <DialogDescription className="max-w-[330px] text-[13px] leading-[19px] text-[#71717A]">
                {passwordStep === 1
                  ? "Önce hesabınıza ait e-posta adresini doğrulayın."
                  : "E-postanıza gelen kodu ve yeni şifrenizi girin."}
              </DialogDescription>

              <div className="pt-2">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-bold text-[#18181B]">
                    {passwordStep === 1 ? "E-posta Doğrulama" : "Yeni Şifre"}
                  </span>
                  <span className="text-[11px] font-bold text-[#A1A1AA]">
                    {passwordStep} / 2
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <span className="h-1 rounded-full bg-[#EA580C]" />
                  <span
                    className={`h-1 rounded-full ${
                      passwordStep === 2 ? "bg-[#EA580C]" : "bg-[#E4E4E7]"
                    }`}
                  />
                </div>
              </div>
            </DialogHeader>

            <div className="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain px-5 py-5 [-webkit-overflow-scrolling:touch]">
              {passwordStep === 1 ? (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label>E-posta</Label>
                    <div className="flex min-h-[52px] items-center rounded-[15px] bg-[#F4F4F5] px-3.5 text-sm text-[#71717A]">
                      <span className="truncate">{passwordEmail}</span>
                    </div>
                  </div>

                  <div className="rounded-[15px] bg-[#FFF7ED] p-3.5">
                    <p className="text-xs font-bold text-[#EA580C]">
                      Doğrulama gerekli
                    </p>
                    <p className="mt-1 text-[11px] leading-[17px] text-[#9A3412]">
                      Şifre değişikliğini onaylamak için bu adrese tek
                      kullanımlık doğrulama kodu göndereceğiz.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="mobile-password-code">
                      Doğrulama kodu
                    </Label>
                    <Input
                      id="mobile-password-code"
                      value={passwordCode}
                      onChange={(e) => setPasswordCode(e.target.value)}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="Mailinize gelen kod"
                      className="h-[52px] rounded-[15px] bg-[#FAFAFA] text-base"
                      required
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="mobile-new-password">Yeni şifre</Label>
                    <Input
                      id="mobile-new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                      placeholder="En az 6 karakter"
                      className="h-[52px] rounded-[15px] bg-[#FAFAFA] text-base"
                      required
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="mobile-new-password-again">
                      Yeni şifre tekrar
                    </Label>
                    <Input
                      id="mobile-new-password-again"
                      type="password"
                      value={newPasswordAgain}
                      onChange={(e) => setNewPasswordAgain(e.target.value)}
                      autoComplete="new-password"
                      placeholder="Yeni şifrenizi tekrar girin"
                      className="h-[52px] rounded-[15px] bg-[#FAFAFA] text-base"
                      required
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="relative z-10 grid shrink-0 grid-cols-2 gap-3 border-t border-[#E4E4E7] bg-white px-5 pt-3 pb-[calc(12px+env(safe-area-inset-bottom))]">
              <Button
                type="button"
                variant="outline"
                disabled={passwordLoading || passwordCodeLoading}
                onClick={() => {
                  if (passwordStep === 1) {
                    closeMobilePanel();
                    return;
                  }
                  setPasswordStep(1);
                }}
                className="h-[52px] rounded-[15px]"
              >
                {passwordStep === 1 ? "Vazgeç" : "Geri"}
              </Button>

              {passwordStep === 1 ? (
                <Button
                  type="button"
                  disabled={passwordCodeLoading || !passwordEmail}
                  onClick={handleSendPasswordCode}
                  className="h-[52px] rounded-[15px] bg-[#EA580C] hover:bg-[#EA580C]"
                >
                  {passwordCodeLoading ? "Gönderiliyor..." : "Kodu Gönder"}
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={passwordLoading}
                  className="h-[52px] rounded-[15px] bg-[#EA580C] hover:bg-[#EA580C]"
                >
                  {passwordLoading ? "Güncelleniyor..." : "Şifreyi Güncelle"}
                </Button>
              )}
            </div>
          </form>
        </DialogContent>
      </Dialog>
      <div
        className="mx-auto hidden w-full max-w-[1180px] space-y-6 md:block"
        data-testid="profile-settings-page"
      >
        <section className="relative overflow-hidden rounded-[30px] border border-zinc-200/80 bg-white p-7 shadow-[0_22px_65px_rgba(24,24,27,0.05)]">
          <div
            aria-hidden="true"
            className="absolute -right-16 -top-20 h-64 w-64 rounded-full bg-orange-100/80 blur-3xl"
          />

          <div className="relative flex items-start justify-between gap-8">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3.5 py-2 text-[11px] font-extrabold uppercase tracking-[0.13em] text-orange-700">
                <Building2 className="h-3.5 w-3.5" />
                İşletme Ayarları
              </div>

              <h1
                className="mt-5 text-[34px] font-extrabold tracking-[-0.04em] text-zinc-950 lg:text-[40px]"
                data-testid="settings-heading"
              >
                Web Menü profilinizi güncel tutun.
              </h1>

              <p className="mt-3 max-w-xl text-sm font-medium leading-6 text-zinc-500">
                İşletme bilgilerinizi, müşteri yönlendirmelerinizi ve hesap
                güvenliğinizi tek ekrandan yönetin.
              </p>
            </div>

            <div className="flex min-w-[270px] items-center gap-4 rounded-[22px] border border-zinc-200 bg-[#FAFAF9] p-4">
              {businessLogo ? (
                <img
                  src={businessLogo}
                  alt={formData.name || "İşletme logosu"}
                  className="h-14 w-14 shrink-0 rounded-[16px] border border-zinc-200 bg-white object-cover"
                />
              ) : (
                <div className="grid h-14 w-14 shrink-0 place-items-center rounded-[16px] bg-[#EA580C] text-xl font-extrabold text-white">
                  {formData.name.trim().charAt(0).toLocaleUpperCase("tr-TR") || "İ"}
                </div>
              )}

              <div className="min-w-0">
                <p className="truncate text-sm font-extrabold text-zinc-950">
                  {formData.name || "İşletmeniz"}
                </p>
                <p className="mt-1 truncate text-xs font-semibold text-zinc-500">
                  {selectedSectorName}
                </p>
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[9px] font-extrabold uppercase tracking-[0.1em] text-emerald-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Profil aktif
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="grid items-start gap-6 xl:grid-cols-[1.18fr_0.82fr]">
          <form
            onSubmit={handleSubmit}
            className="space-y-6"
            data-testid="settings-form"
          >
            <section className="rounded-[28px] border border-zinc-200/80 bg-white p-6 shadow-[0_18px_55px_rgba(24,24,27,0.04)]">
              <div className="flex items-start gap-4 border-b border-zinc-100 pb-5">
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-[14px] bg-orange-50 text-[#EA580C]">
                  <Building2 className="h-5 w-5" />
                </div>

                <div>
                  <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#EA580C]">
                    İşletme Profili
                  </p>
                  <h2 className="mt-1 text-xl font-extrabold tracking-[-0.025em] text-zinc-950">
                    Temel işletme bilgileri
                  </h2>
                  <p className="mt-1 text-sm font-medium text-zinc-500">
                    Müşterilerinizin Web Menü vitrininizde göreceği bilgileri düzenleyin.
                  </p>
                </div>
              </div>

              <div className="mt-6 grid gap-5 lg:grid-cols-2">
                <div className="space-y-2 lg:col-span-2">
                  <Label htmlFor="name" className="text-xs font-bold text-zinc-700">
                    İşletme Adı
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => updateFormField("name", e.target.value)}
                    required
                    className="h-12 rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 focus-visible:ring-orange-200"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs font-bold text-zinc-700">
                    E-posta
                  </Label>
                  <Input
                    id="email"
                    value={formData.email}
                    onChange={(e) => updateFormField("email", e.target.value)}
                    placeholder="ornek@isletmeniz.com"
                    required
                    className="h-12 rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 focus-visible:ring-orange-200"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="type_constant_value"
                    className="text-xs font-bold text-zinc-700"
                  >
                    İşletme Türü <span className="text-red-500">*</span>
                  </Label>

                  <Select
                    value={formData.type_constant_value}
                    onValueChange={(value) =>
                      updateFormField("type_constant_value", value)
                    }
                    disabled={constantsLoading}
                  >
                    <SelectTrigger
                      id="type_constant_value"
                      className="h-12 rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4"
                    >
                      <SelectValue
                        placeholder={
                          constantsLoading
                            ? "İşletme türleri yükleniyor..."
                            : "İşletme türünü seçin"
                        }
                      />
                    </SelectTrigger>

                    <SelectContent
                      position="popper"
                      side="bottom"
                      align="start"
                      sideOffset={6}
                      className="z-50 max-h-[260px] w-[var(--radix-select-trigger-width)] overflow-y-auto rounded-xl border border-zinc-200 bg-white p-1 shadow-xl"
                    >
                      {businessSectors.map((item) => (
                        <SelectItem
                          key={item.value}
                          value={item.value}
                          className="cursor-pointer rounded-lg px-3 py-2 text-sm"
                        >
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="contact_phone"
                    className="text-xs font-bold text-zinc-700"
                  >
                    İletişim Telefonu
                  </Label>
                  <Input
                    id="contact_phone"
                    value={formData.contact_phone}
                    onChange={(e) =>
                      updateFormField("contact_phone", e.target.value)
                    }
                    className="h-12 rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 focus-visible:ring-orange-200"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold text-zinc-700">
                    İşletme Logosu
                  </Label>

                  <input
                    id="desktop-logo"
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                  />

                  <label
                    htmlFor="desktop-logo"
                    className="flex h-12 cursor-pointer items-center justify-between gap-3 rounded-[14px] border border-zinc-200 bg-zinc-50/70 px-4 transition hover:border-orange-200 hover:bg-white"
                  >
                    <span className="min-w-0 truncate text-sm font-medium text-zinc-600">
                      {logoFile ? logoFile.name : "Logo seçin"}
                    </span>

                    <span className="inline-flex shrink-0 items-center gap-1.5 text-xs font-bold text-[#EA580C]">
                      <Upload className="h-4 w-4" />
                      {logoFile ? "Değiştir" : "Seç"}
                    </span>
                  </label>
                </div>

                <div className="space-y-2 lg:col-span-2">
                  <Label
                    htmlFor="contact_address"
                    className="text-xs font-bold text-zinc-700"
                  >
                    Adres
                  </Label>
                  <Textarea
                    id="contact_address"
                    value={formData.contact_address}
                    onChange={(e) =>
                      updateFormField("contact_address", e.target.value)
                    }
                    className="min-h-[96px] resize-none rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 py-3 focus-visible:ring-orange-200"
                  />
                </div>
              </div>
            </section>

            <section className="rounded-[28px] border border-zinc-200/80 bg-white p-6 shadow-[0_18px_55px_rgba(24,24,27,0.04)]">
              <div className="flex items-start justify-between gap-5 border-b border-zinc-100 pb-5">
                <div className="flex items-start gap-4">
                  <div className="grid h-11 w-11 shrink-0 place-items-center rounded-[14px] bg-blue-50 text-blue-600">
                    <MapPin className="h-5 w-5" />
                  </div>

                  <div>
                    <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-blue-600">
                      Müşteri Deneyimi
                    </p>
                    <h2 className="mt-1 text-xl font-extrabold tracking-[-0.025em] text-zinc-950">
                      Vitrin yönlendirmeleri
                    </h2>
                    <p className="mt-1 text-sm font-medium text-zinc-500">
                      Değerlendirme, sosyal medya ve konum bağlantılarınızı yönetin.
                    </p>
                  </div>
                </div>

                {popupConfigured ? (
                  <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.1em] text-emerald-700">
                    Hazır
                  </span>
                ) : (
                  <span className="rounded-full bg-zinc-100 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.1em] text-zinc-500">
                    Kurulum bekliyor
                  </span>
                )}
              </div>

              <div className="mt-6 space-y-5">
                <div className="space-y-2">
                  <Label
                    htmlFor="popup_title"
                    className="text-xs font-bold text-zinc-700"
                  >
                    Popup Başlığı
                  </Label>
                  <Input
                    id="popup_title"
                    value={formData.popup_title}
                    onChange={(e) =>
                      updateFormField("popup_title", e.target.value)
                    }
                    placeholder="Bizi değerlendirin ve takip edin"
                    className="h-12 rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 focus-visible:ring-orange-200"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="popup_description"
                    className="text-xs font-bold text-zinc-700"
                  >
                    Popup Açıklaması
                  </Label>
                  <Textarea
                    id="popup_description"
                    value={formData.popup_description}
                    onChange={(e) =>
                      updateFormField("popup_description", e.target.value)
                    }
                    className="min-h-[110px] resize-none rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 py-3 focus-visible:ring-orange-200"
                    placeholder="Müşterilerinize kısa bir mesaj yazın"
                  />
                </div>

                <div className="grid gap-5 lg:grid-cols-2">
                  <div className="space-y-2">
                    <Label
                      htmlFor="instagram_url"
                      className="text-xs font-bold text-zinc-700"
                    >
                      Instagram Linki
                    </Label>
                    <Input
                      id="instagram_url"
                      value={formData.instagram_url}
                      onChange={(e) =>
                        updateFormField("instagram_url", e.target.value)
                      }
                      placeholder="https://instagram.com/isletmehesabi"
                      className="h-12 rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 focus-visible:ring-orange-200"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="review_url"
                      className="text-xs font-bold text-zinc-700"
                    >
                      Değerlendirme Linki
                    </Label>
                    <Input
                      id="review_url"
                      value={formData.review_url}
                      onChange={(e) =>
                        updateFormField("review_url", e.target.value)
                      }
                      placeholder="Google yorum / değerlendirme linki"
                      className="h-12 rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 focus-visible:ring-orange-200"
                    />
                  </div>

                  <div className="space-y-2 lg:col-span-2">
                    <Label
                      htmlFor="maps_url"
                      className="text-xs font-bold text-zinc-700"
                    >
                      Konum Linki
                    </Label>
                    <Input
                      id="maps_url"
                      value={formData.maps_url}
                      onChange={(e) =>
                        updateFormField("maps_url", e.target.value)
                      }
                      placeholder="https://maps.google.com/..."
                      className="h-12 rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 focus-visible:ring-orange-200"
                    />
                  </div>
                </div>

                <div className="rounded-[18px] border border-orange-100 bg-orange-50/60 p-4">
                  <p className="text-xs font-bold text-orange-700">
                    Web Menü müşteri deneyimi
                  </p>
                  <p className="mt-1 text-[11px] font-medium leading-5 text-orange-900/70">
                    Boş bıraktığınız bağlantılar müşterilerinize gösterilmez.
                  </p>
                </div>
              </div>
            </section>

            <div className="sticky bottom-5 z-10 flex items-center justify-between gap-5 rounded-[22px] border border-zinc-200 bg-white/95 p-4 shadow-[0_18px_55px_rgba(24,24,27,0.12)] backdrop-blur">
              <div>
                <p className="text-sm font-extrabold text-zinc-950">
                  Değişiklikleri yayınlamaya hazır mısınız?
                </p>
                <p className="mt-1 text-xs font-medium text-zinc-500">
                  İşletme profiliniz ve müşteri deneyimi birlikte güncellenir.
                </p>
              </div>

              <Button
                type="submit"
                className="h-11 shrink-0 rounded-full bg-[#EA580C] px-6 font-bold text-white hover:bg-[#C2410C]"
                disabled={loading}
              >
                {loading ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}
              </Button>
            </div>
          </form>

          <div className="space-y-6">
            <section className="rounded-[28px] border border-zinc-200/80 bg-white p-6 shadow-[0_18px_55px_rgba(24,24,27,0.04)]">
              <div className="flex items-start gap-4">
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-[14px] bg-zinc-100 text-zinc-600">
                  <KeyRound className="h-5 w-5" />
                </div>

                <div>
                  <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-zinc-400">
                    Hesap Güvenliği
                  </p>
                  <h2 className="mt-1 text-xl font-extrabold tracking-[-0.025em] text-zinc-950">
                    Şifrenizi güncelleyin
                  </h2>
                  <p className="mt-1 text-sm font-medium leading-6 text-zinc-500">
                    Şifre değişikliğinden önce e-posta doğrulama kodu gerekir.
                  </p>
                </div>
              </div>

              <form
                onSubmit={handleChangePassword}
                className="mt-6 space-y-4"
                data-testid="password-change-form"
              >
                <div className="rounded-[18px] border border-zinc-200 bg-[#FAFAF9] p-4">
                  <div className="space-y-2">
                    <Label
                      htmlFor="passwordEmail"
                      className="text-xs font-bold text-zinc-700"
                    >
                      E-posta
                    </Label>
                    <Input
                      id="passwordEmail"
                      value={passwordEmail}
                      disabled
                      className="h-11 rounded-[13px] border-zinc-200 bg-white px-4"
                    />
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="mt-3 h-11 w-full rounded-full border-zinc-200 bg-white font-bold text-zinc-800 hover:bg-zinc-50"
                    onClick={handleSendPasswordCode}
                    disabled={passwordCodeLoading}
                  >
                    {passwordCodeLoading
                      ? "Kod Gönderiliyor..."
                      : "Doğrulama Kodu Gönder"}
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="passwordCode"
                    className="text-xs font-bold text-zinc-700"
                  >
                    Doğrulama Kodu
                  </Label>
                  <Input
                    id="passwordCode"
                    value={passwordCode}
                    onChange={(e) => setPasswordCode(e.target.value)}
                    placeholder="E-postanıza gelen kod"
                    required
                    className="h-11 rounded-[13px] border-zinc-200 bg-zinc-50/70 px-4 focus-visible:ring-orange-200"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="newPassword"
                    className="text-xs font-bold text-zinc-700"
                  >
                    Yeni Şifre
                  </Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Yeni şifreniz"
                    required
                    className="h-11 rounded-[13px] border-zinc-200 bg-zinc-50/70 px-4 focus-visible:ring-orange-200"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="newPasswordAgain"
                    className="text-xs font-bold text-zinc-700"
                  >
                    Yeni Şifre (Tekrar)
                  </Label>
                  <Input
                    id="newPasswordAgain"
                    type="password"
                    value={newPasswordAgain}
                    onChange={(e) => setNewPasswordAgain(e.target.value)}
                    placeholder="Yeni şifreyi tekrar yazın"
                    required
                    className="h-11 rounded-[13px] border-zinc-200 bg-zinc-50/70 px-4 focus-visible:ring-orange-200"
                  />
                </div>

                <Button
                  type="submit"
                  className="h-11 w-full rounded-full bg-zinc-950 font-bold text-white hover:bg-zinc-800"
                  disabled={passwordLoading}
                >
                  {passwordLoading
                    ? "Şifre Güncelleniyor..."
                    : "Şifreyi Güncelle"}
                </Button>
              </form>
            </section>

            <section className="rounded-[28px] bg-zinc-950 p-6 text-white shadow-[0_22px_60px_rgba(24,24,27,0.14)]">
              <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-orange-300">
                Profil Özeti
              </p>

              <div className="mt-5 space-y-4">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-500">
                    İşletme
                  </p>
                  <p className="mt-1 text-sm font-extrabold">
                    {formData.name || "İşletmeniz"}
                  </p>
                </div>

                <div className="h-px bg-white/10" />

                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-500">
                    Sektör
                  </p>
                  <p className="mt-1 text-sm font-semibold text-zinc-200">
                    {selectedSectorName}
                  </p>
                </div>

                <div className="h-px bg-white/10" />

                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-500">
                    Müşteri yönlendirmeleri
                  </p>
                  <p className="mt-1 text-sm font-semibold text-zinc-200">
                    {popupConfigured ? "Yapılandırıldı" : "Henüz yapılandırılmadı"}
                  </p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
